import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const CreateInvoiceModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    client: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    designs: [],
    paymentTerms: '30',
    notes: ''
  });

  const [selectedDesigns, setSelectedDesigns] = useState([]);

  const clientOptions = [
    { value: 'ajio', label: 'AJIO', description: 'Online Platform' },
    { value: 'myntra', label: 'MYNTRA', description: 'Online Platform' },
    { value: 'fashion-hub', label: 'Fashion Hub Pvt Ltd', description: 'Offline Client' },
    { value: 'style-store', label: 'Style Store', description: 'Offline Client' },
    { value: 'trendy-wear', label: 'Trendy Wear Co.', description: 'Offline Client' }
  ];

  const paymentTermsOptions = [
    { value: '15', label: '15 Days' },
    { value: '30', label: '30 Days' },
    { value: '45', label: '45 Days' },
    { value: '60', label: '60 Days' }
  ];

  const availableDesigns = [
    { id: 1, name: 'Floral Print', pieces: 50, rate: 250 },
    { id: 2, name: 'Abstract Design', pieces: 75, rate: 300 },
    { id: 3, name: 'Geometric Pattern', pieces: 100, rate: 280 },
    { id: 4, name: 'Vintage Style', pieces: 60, rate: 320 },
    { id: 5, name: 'Summer Collection', pieces: 120, rate: 200 },
    { id: 6, name: 'Casual Wear', pieces: 80, rate: 180 },
    { id: 7, name: 'Ethnic Wear', pieces: 90, rate: 350 },
    { id: 8, name: 'Traditional Print', pieces: 70, rate: 400 }
  ];

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-calculate due date when client or issue date changes
    if (field === 'client' || field === 'issueDate') {
      const selectedClient = clientOptions.find(c => c.value === (field === 'client' ? value : formData.client));
      const isOnline = selectedClient?.description.includes('Online');
      const days = isOnline ? 15 : parseInt(formData.paymentTerms);
      
      const issueDate = new Date(field === 'issueDate' ? value : formData.issueDate);
      const dueDate = new Date(issueDate);
      dueDate.setDate(dueDate.getDate() + days);
      
      setFormData(prev => ({
        ...prev,
        dueDate: dueDate.toISOString().split('T')[0],
        paymentTerms: isOnline ? '15' : prev.paymentTerms
      }));
    }
  };

  const addDesign = (design) => {
    if (!selectedDesigns.find(d => d.id === design.id)) {
      setSelectedDesigns([...selectedDesigns, { ...design, quantity: 1 }]);
    }
  };

  const removeDesign = (designId) => {
    setSelectedDesigns(selectedDesigns.filter(d => d.id !== designId));
  };

  const updateDesignQuantity = (designId, quantity) => {
    setSelectedDesigns(selectedDesigns.map(d => 
      d.id === designId ? { ...d, quantity: Math.max(1, quantity) } : d
    ));
  };

  const calculateTotal = () => {
    const subtotal = selectedDesigns.reduce((sum, design) => sum + (design.rate * design.quantity), 0);
    const cgst = subtotal * 0.09;
    const sgst = subtotal * 0.09;
    return {
      subtotal,
      cgst,
      sgst,
      total: subtotal + cgst + sgst
    };
  };

  const handleSave = () => {
    if (!formData.client || selectedDesigns.length === 0) {
      alert('Please select a client and add at least one design');
      return;
    }

    const totals = calculateTotal();
    const invoiceData = {
      ...formData,
      designs: selectedDesigns,
      amount: totals.total,
      items: selectedDesigns.reduce((sum, d) => sum + d.quantity, 0)
    };

    onSave(invoiceData);
    onClose();
    
    // Reset form
    setFormData({
      client: '',
      issueDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      designs: [],
      paymentTerms: '30',
      notes: ''
    });
    setSelectedDesigns([]);
  };

  const totals = calculateTotal();

  return (
    <div className="fixed inset-0 bg-black/50 z-1001 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="FileText" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Create New Invoice</h2>
              <p className="text-sm text-muted-foreground">Generate invoice for client orders</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Basic Info */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Invoice Details</h3>
                <div className="space-y-4">
                  <Select
                    label="Select Client"
                    placeholder="Choose client"
                    options={clientOptions}
                    value={formData.client}
                    onChange={(value) => handleInputChange('client', value)}
                    required
                    searchable
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Issue Date"
                      type="date"
                      value={formData.issueDate}
                      onChange={(e) => handleInputChange('issueDate', e.target.value)}
                      required
                    />

                    <Input
                      label="Due Date"
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => handleInputChange('dueDate', e.target.value)}
                      required
                    />
                  </div>

                  <Select
                    label="Payment Terms"
                    placeholder="Select payment terms"
                    options={paymentTermsOptions}
                    value={formData.paymentTerms}
                    onChange={(value) => handleInputChange('paymentTerms', value)}
                  />

                  <Input
                    label="Notes (Optional)"
                    type="text"
                    placeholder="Additional notes or instructions"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                  />
                </div>
              </div>

              {/* Available Designs */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Available Designs</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availableDesigns.map((design) => (
                    <div key={design.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/30 transition-smooth">
                      <div className="flex-1">
                        <div className="font-medium text-foreground">{design.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {design.pieces} pieces available • ₹{design.rate}/piece
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addDesign(design)}
                        disabled={selectedDesigns.find(d => d.id === design.id)}
                        iconName="Plus"
                        iconSize={16}
                      >
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Selected Designs & Totals */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Selected Designs</h3>
                {selectedDesigns.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Icon name="Package" size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No designs selected</p>
                    <p className="text-sm">Add designs from the left panel</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedDesigns.map((design) => (
                      <div key={design.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium text-foreground">{design.name}</div>
                          <div className="text-sm text-muted-foreground">₹{design.rate}/piece</div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => updateDesignQuantity(design.id, design.quantity - 1)}
                            iconName="Minus"
                            iconSize={14}
                          />
                          <span className="w-12 text-center font-medium">{design.quantity}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => updateDesignQuantity(design.id, design.quantity + 1)}
                            iconName="Plus"
                            iconSize={14}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeDesign(design.id)}
                            iconName="Trash2"
                            iconSize={14}
                            className="text-error hover:text-error ml-2"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Invoice Totals */}
              {selectedDesigns.length > 0 && (
                <div className="bg-muted/30 border border-border rounded-lg p-4">
                  <h4 className="font-semibold text-foreground mb-3">Invoice Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>₹{totals.subtotal.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>CGST (9%):</span>
                      <span>₹{totals.cgst.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>SGST (9%):</span>
                      <span>₹{totals.sgst.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="border-t border-border pt-2 flex justify-between font-semibold text-lg">
                      <span>Total Amount:</span>
                      <span>₹{totals.total.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="default" 
            onClick={handleSave}
            disabled={!formData.client || selectedDesigns.length === 0}
            iconName="Save"
            iconPosition="left"
          >
            Create Invoice
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateInvoiceModal;